//frontend/src/components/gro/RiskInventoryItemsModerationTable.tsx
import { useMemo, useState } from "react";
import {
  BarChart3,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleDot,
  ClipboardList,
  Clock3,
  Gauge,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  ScrollText,
  Rows3,
  ShieldCheck,
  ShieldX,
  Trophy,
  Trash2,
  Undo2,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { RiskInventoryItem } from "@/types/riskInventory";

type Props = {
  items: RiskInventoryItem[];
  inventoryName?: string | null;
  canWrite?: boolean;
  busy?: boolean;
  onLogs?: (item: RiskInventoryItem) => void;
  onActionPlans?: (item: RiskInventoryItem) => void;
  onEdit?: (item: RiskInventoryItem) => void;
  onMitigate?: (item: RiskInventoryItem) => void;
  onClose?: (item: RiskInventoryItem) => void;
  onReopen?: (item: RiskInventoryItem) => void;
  onDelete?: (item: RiskInventoryItem) => void;
  onRemoveFromInventory?: (item: RiskInventoryItem) => void;
};

type RiskTone = { text: string; badgeClass: string; boxClass: string; iconClass: string; accentClass: string };

function normalizeRiskCode(item: RiskInventoryItem): "low" | "moderate" | "high" | "critical" {
  const code = (item.risk_level_code || "").toLowerCase();
  if (code === "critical" || code === "high" || code === "moderate" || code === "low") return code;

  const level = (item.risk_level || "").toLowerCase();
  if (level.includes("crít")) return "critical";
  if (level.includes("alto")) return "high";
  if (level.includes("moder")) return "moderate";
  return "low";
}

function riskPresentation(item: RiskInventoryItem): RiskTone {
  const level = normalizeRiskCode(item);
  if (level === "critical") {
    return {
      text: "Risco Crítico",
      badgeClass: "border-red-200 bg-red-50 text-red-700",
      boxClass: "border-red-200 bg-red-50/70 text-red-700",
      iconClass: "bg-red-100 text-red-700",
      accentClass: "before:bg-red-400",
    };
  }
  if (level === "high") {
    return {
      text: "Risco Alto",
      badgeClass: "border-amber-200 bg-amber-50 text-amber-700",
      boxClass: "border-amber-200 bg-amber-50/70 text-amber-700",
      iconClass: "bg-amber-100 text-amber-700",
      accentClass: "before:bg-amber-400",
    };
  }
  if (level === "moderate") {
    return {
      text: "Risco Moderado",
      badgeClass: "border-slate-200 bg-slate-100 text-slate-700",
      boxClass: "border-slate-200 bg-slate-100/80 text-slate-700",
      iconClass: "bg-slate-100 text-slate-700",
      accentClass: "before:bg-slate-400",
    };
  }
  return {
    text: "Risco Baixo",
    badgeClass: "border-emerald-200 bg-emerald-50 text-emerald-700",
    boxClass: "border-emerald-200 bg-emerald-50/70 text-emerald-700",
    iconClass: "bg-emerald-100 text-emerald-700",
    accentClass: "before:bg-emerald-400",
  };
}

function toneFromScore(score?: number | null): RiskTone {
  if (typeof score !== "number") {
    return riskPresentation({ risk_level: "", risk_level_code: "low" } as RiskInventoryItem);
  }
  if (score >= 4) return riskPresentation({ risk_level: "", risk_level_code: "high" } as RiskInventoryItem);
  if (score === 3) return riskPresentation({ risk_level: "", risk_level_code: "moderate" } as RiskInventoryItem);
  return riskPresentation({ risk_level: "", risk_level_code: "low" } as RiskInventoryItem);
}

function itemStatusBadgeClass(status: RiskInventoryItem["status"]) {
  if (status === "closed") return "border-red-200 bg-red-50 text-red-700";
  if (status === "mitigated") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-emerald-200 bg-emerald-50 text-emerald-700";
}

function itemStatusLabel(status: RiskInventoryItem["status"]) {
  if (status === "closed") return "Encerrado";
  if (status === "mitigated") return "Mitigado";
  return "Aberto";
}

function cutTypeLabel(cutType?: RiskInventoryItem["cut_type"] | null) {
  if (cutType === "unit") return "Unidade";
  if (cutType === "department") return "Departamento";
  if (cutType === "job_position") return "Cargo";
  if (cutType === "worker_group") return "Grupo de Avaliação";
  return "-";
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
}

function scoreValue(prob?: number | null, sev?: number | null) {
  if (typeof prob !== "number" || typeof sev !== "number") return null;
  return Math.round(prob * sev);
}

function MetadataField({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="flex min-w-0 items-start gap-3 py-1.5">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700 ring-1 ring-violet-100">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 space-y-0.5">
        <div className="text-xs font-medium leading-4 text-muted-foreground">{label}</div>
        <div className="break-words text-sm font-semibold leading-5 text-foreground">{value || "-"}</div>
      </div>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  helper,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string | number;
  helper: string;
  tone: RiskTone;
}) {
  return (
    <div className={`relative overflow-hidden rounded-xl border bg-white p-3.5 shadow-sm before:absolute before:inset-y-0 before:left-0 before:w-1 ${tone.boxClass} ${tone.accentClass}`}>
      <div className="flex items-center gap-3">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone.iconClass}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold leading-4 text-current/85">{label}</div>
          <div className="truncate text-base font-bold leading-6 text-foreground">{value}</div>
          <div className="truncate text-xs leading-4 text-current/75">{helper}</div>
        </div>
      </div>
    </div>
  );
}

export function RiskInventoryItemsModerationTable({
  items,
  inventoryName,
  canWrite = false,
  busy = false,
  onLogs,
  onActionPlans,
  onEdit,
  onMitigate,
  onClose,
  onReopen,
  onDelete,
  onRemoveFromInventory,
}: Props) {
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.title || "").localeCompare(b.title || "", "pt-BR")),
    [items],
  );

  if (!sortedItems.length) {
    return <Card className="p-6 text-sm text-muted-foreground">Nenhum item encontrado para os filtros informados.</Card>;
  }

  return (
    <div className="space-y-4">
      {sortedItems.map((item) => {
        const isOpen = !!openMap[item.id];
        const risk = riskPresentation(item);
        const probabilityTone = toneFromScore(item.probability_score);
        const severityTone = toneFromScore(item.severity_score);
        const resolvedInventoryName = item.inventory_name || inventoryName || "-";
        const canDeleteManual = canWrite && item.risk_source_type === "manual";
        const canRemoveSuggestion = canWrite && item.risk_source_type === "survey_suggestion";

        const metadata = [
          { icon: CalendarDays, label: "Campanha", value: item.campaign_name || "-" },
          { icon: UsersRound, label: "Dimensão", value: item.dimension_name || "-" },
          { icon: Building2, label: "Corte", value: item.cut_name ? `${cutTypeLabel(item.cut_type)} • ${item.cut_name}` : "-" },
          { icon: Clock3, label: "Criada em", value: formatDateTime(item.created_at) },
          { icon: ClipboardList, label: "Inventário", value: resolvedInventoryName },
          { icon: CircleDot, label: "Origem", value: item.risk_source_type === "normex_copilot" ? "Normex Copilot" : item.risk_source_type === "manual" ? "Manual" : "Campanha" },
        ];

        return (
          <Collapsible key={item.id} open={isOpen}>
            <Card className="overflow-hidden rounded-2xl border-border/80 bg-white shadow-sm transition-shadow hover:shadow-md">
              <div className="flex flex-col gap-4 border-b bg-gradient-to-r from-white via-white to-slate-50/80 p-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="break-words text-lg font-bold leading-6 text-slate-950">
                        Risco: {item.title || "Item de risco"}
                      </h3>
                      <Badge variant="outline" className={risk.badgeClass}>
                        {item.risk_level || risk.text}
                      </Badge>
                      <Badge variant="outline" className={itemStatusBadgeClass(item.status)}>
                        {itemStatusLabel(item.status)}
                      </Badge>
                      {item.has_action_plan ? (
                        <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
                          Com Plano de Ação
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-2 lg:justify-end">
                  <Button variant="outline" onClick={() => onLogs?.(item)} disabled={busy} className="h-10 rounded-xl">
                    <ScrollText className="mr-2 h-4 w-4" />
                    Logs
                  </Button>

                  <Button variant="outline" onClick={() => onActionPlans?.(item)} disabled={busy} className="h-10 rounded-xl">
                    <Rows3 className="mr-2 h-4 w-4" />
                    Planos de Ação
                  </Button>

                  {(canWrite || canDeleteManual || canRemoveSuggestion) ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" aria-label="Mais ações" disabled={busy} className="h-10 w-10 rounded-xl">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {canWrite ? (
                          <DropdownMenuItem onClick={() => onEdit?.(item)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Editar item
                          </DropdownMenuItem>
                        ) : null}

                        {canWrite && item.status !== "mitigated" ? (
                          <DropdownMenuItem onClick={() => onMitigate?.(item)}>
                            <ShieldX className="mr-2 h-4 w-4" />
                            Mitigar
                          </DropdownMenuItem>
                        ) : null}

                        {canWrite && item.status !== "closed" ? (
                          <DropdownMenuItem onClick={() => onClose?.(item)}>
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            Encerrar
                          </DropdownMenuItem>
                        ) : null}

                        {canWrite && item.status !== "open" ? (
                          <DropdownMenuItem onClick={() => onReopen?.(item)}>
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Reabrir
                          </DropdownMenuItem>
                        ) : null}

                        {(canDeleteManual || canRemoveSuggestion) ? <DropdownMenuSeparator /> : null}

                        {canDeleteManual ? (
                          <DropdownMenuItem onClick={() => onDelete?.(item)} className="text-red-600 focus:text-red-600">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Excluir item manual
                          </DropdownMenuItem>
                        ) : null}

                        {canRemoveSuggestion ? (
                          <DropdownMenuItem onClick={() => onRemoveFromInventory?.(item)} className="text-red-600 focus:text-red-600">
                            <Undo2 className="mr-2 h-4 w-4" />
                            Remover do inventário
                          </DropdownMenuItem>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
              </div>

              <div className="space-y-4 p-4">
                <div className="grid gap-x-6 gap-y-2 md:grid-cols-2 xl:grid-cols-3">
                  {metadata.map((field) => (
                    <MetadataField key={`${item.id}-${field.label}`} icon={field.icon} label={field.label} value={field.value} />
                  ))}
                </div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <MetricCard
                    icon={BarChart3}
                    label="Probabilidade"
                    value={item.probability_level || "-"}
                    helper={`Score: ${item.probability_score ?? "-"}`}
                    tone={probabilityTone}
                  />
                  <MetricCard
                    icon={ShieldCheck}
                    label="Severidade"
                    value={item.severity_level || "-"}
                    helper={`Score: ${item.severity_score ?? "-"}`}
                    tone={severityTone}
                  />
                  <MetricCard
                    icon={Gauge}
                    label="Nível do risco"
                    value={item.risk_level || risk.text}
                    helper="Classificação atual"
                    tone={risk}
                  />
                  <MetricCard
                    icon={Trophy}
                    label="Score final"
                    value={scoreValue(item.probability_score, item.severity_score) ?? "-"}
                    helper="Probabilidade × Severidade"
                    tone={{
                      text: "Score final",
                      badgeClass: "border-violet-200 bg-violet-50 text-violet-700",
                      boxClass: "border-violet-200 bg-violet-50/80 text-violet-700",
                      iconClass: "bg-violet-100 text-violet-700",
                      accentClass: "before:bg-violet-400",
                    }}
                  />
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      setOpenMap((prev) => ({
                        ...prev,
                        [item.id]: !prev[item.id],
                      }))
                    }
                  >
                    {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {isOpen ? "Ocultar detalhes" : "Ver detalhes"}
                  </Button>
                </div>

                <CollapsibleContent>
                  <div className="grid gap-3 border-t pt-4 md:grid-cols-2">
                    <div className="rounded-lg border bg-muted/30 p-3 text-sm leading-6">
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Descrição</div>
                      <div className="mt-2 text-foreground">{item.description?.trim() || "Sem descrição cadastrada."}</div>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3 text-sm leading-6">
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Possíveis danos</div>
                      <div className="mt-2 text-foreground">{item.possible_damage?.trim() || "-"}</div>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3 text-sm leading-6">
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Diretrizes de controle</div>
                      <div className="mt-2 text-foreground">{item.control_guidelines?.trim() || "-"}</div>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3 text-sm leading-6">
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Exemplos de situação</div>
                      <div className="mt-2 text-foreground">{item.example_situations?.trim() || "-"}</div>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Card>
          </Collapsible>
        );
      })}
    </div>
  );
}
